import { useState } from 'react';
import { updatePassword } from '../lib/updatePassword';

interface ResetPasswordProps {
  recoverySessionReady: boolean;
  checkingRecoverySession: boolean;
  onRequestAnotherLink: () => void;
  onGoToLogin: () => void;
  onPasswordChanged: () => void;
}

const ResetPassword: React.FC<ResetPasswordProps> = ({
  recoverySessionReady,
  checkingRecoverySession,
  onRequestAnotherLink,
  onGoToLogin,
  onPasswordChanged,
}) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const passwordRequirements = [
    { label: 'At least 12 characters', met: newPassword.length >= 12 },
    { label: 'An uppercase letter', met: /[A-Z]/.test(newPassword) },
    { label: 'A lowercase letter', met: /[a-z]/.test(newPassword) },
    { label: 'A number', met: /[0-9]/.test(newPassword) },
    { label: 'A special character', met: /[^A-Za-z0-9]/.test(newPassword) },
  ];

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    if (!recoverySessionReady) {
      setError('This password reset session is no longer valid. Request a new reset link.');
      return;
    }
    if (passwordRequirements.some((requirement) => !requirement.met)) {
      setError('Your password must meet all of the requirements below.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('The passwords do not match.');
      return;
    }

    setSubmitting(true);
    try {
      await updatePassword(newPassword);
      setSuccess(true);
      setNewPassword('');
      setConfirmPassword('');
      onPasswordChanged();
    } catch {
      setError('Unable to update your password. Your reset link may have expired. Request a new link and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-md">
        <h2 className="mb-2 text-2xl font-semibold">Reset Password</h2>
        {checkingRecoverySession ? (
          <p role="status" className="py-4 text-sm text-gray-600">Validating your password reset link…</p>
        ) : success ? (
          <div className="space-y-4">
            <p role="status" className="text-sm leading-relaxed text-green-800">Your password has been updated successfully.</p>
            <button
              type="button"
              onClick={onGoToLogin}
              className="min-h-[48px] w-full rounded-md bg-mainPink px-4 py-3 font-medium text-white transition-colors hover:bg-mainBlue"
            >
              Return to login
            </button>
          </div>
        ) : recoverySessionReady ? (
          <form onSubmit={(event) => void submit(event)} className="space-y-4">
            <div>
              <label htmlFor="new-password" className="block text-sm font-medium text-gray-700">New Password</label>
              <div className="relative mt-1">
                <input
                  id="new-password"
                  type={showNewPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  minLength={12}
                  required
                  value={newPassword}
                  onChange={(event) => setNewPassword(event.target.value)}
                  className="block min-h-[44px] w-full rounded border border-gray-200 px-3 py-2 pr-12 focus:outline-none focus:ring-2 focus:ring-mainBlue"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword((visible) => !visible)}
                  aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                  aria-pressed={showNewPassword}
                  className="absolute inset-y-0 right-0 flex min-h-[44px] w-11 items-center justify-center text-mainBlue transition-colors hover:text-mainPink focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-mainBlue"
                >
                  {showNewPassword ? (
                    <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" className="h-5 w-5" stroke="currentColor" strokeWidth="1.8">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 3l18 18M10.6 10.6a2 2 0 002.8 2.8" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9.9 5.2A10.8 10.8 0 0112 5c5.2 0 8.6 4.7 9.5 6.2a1.5 1.5 0 010 1.6 14 14 0 01-3.1 3.5M6.2 6.2a14 14 0 00-3.7 5 1.5 1.5 0 000 1.6C3.4 14.3 6.8 19 12 19c1.2 0 2.3-.3 3.3-.7" />
                    </svg>
                  ) : (
                    <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" className="h-5 w-5" stroke="currentColor" strokeWidth="1.8">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.5 12s3.4-6.5 9.5-6.5 9.5 6.5 9.5 6.5-3.4 6.5-9.5 6.5S2.5 12 2.5 12z" />
                      <circle cx="12" cy="12" r="2.5" />
                    </svg>
                  )}
                </button>
              </div>
              <ul className="mt-2 space-y-1 text-xs text-gray-600" aria-label="Password requirements">
                {passwordRequirements.map((requirement) => (
                  <li key={requirement.label} className={requirement.met ? 'text-green-700' : undefined}>
                    <span aria-hidden="true">{requirement.met ? '✓' : '•'} </span>{requirement.label}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700">Confirm Password</label>
              <div className="relative mt-1">
                <input
                  id="confirm-password"
                  type={showConfirmPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  minLength={12}
                  required
                  value={confirmPassword}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                  className="block min-h-[44px] w-full rounded border border-gray-200 px-3 py-2 pr-12 focus:outline-none focus:ring-2 focus:ring-mainBlue"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword((visible) => !visible)}
                  aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                  aria-pressed={showConfirmPassword}
                  className="absolute inset-y-0 right-0 flex min-h-[44px] w-11 items-center justify-center text-mainBlue transition-colors hover:text-mainPink focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-mainBlue"
                >
                  {showConfirmPassword ? (
                    <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" className="h-5 w-5" stroke="currentColor" strokeWidth="1.8">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 3l18 18M10.6 10.6a2 2 0 002.8 2.8" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9.9 5.2A10.8 10.8 0 0112 5c5.2 0 8.6 4.7 9.5 6.2a1.5 1.5 0 010 1.6 14 14 0 01-3.1 3.5M6.2 6.2a14 14 0 00-3.7 5 1.5 1.5 0 000 1.6C3.4 14.3 6.8 19 12 19c1.2 0 2.3-.3 3.3-.7" />
                    </svg>
                  ) : (
                    <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" className="h-5 w-5" stroke="currentColor" strokeWidth="1.8">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.5 12s3.4-6.5 9.5-6.5 9.5 6.5 9.5 6.5-3.4 6.5-9.5 6.5S2.5 12 2.5 12z" />
                      <circle cx="12" cy="12" r="2.5" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
            {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
            <button
              type="submit"
              disabled={submitting}
              className="min-h-[48px] w-full rounded-md bg-mainPink py-3.5 font-medium text-white transition-colors hover:bg-mainBlue disabled:opacity-50"
            >
              {submitting ? 'Updating…' : 'Update Password'}
            </button>
          </form>
        ) : (
          <div className="space-y-4">
            <p role="alert" className="text-sm leading-relaxed text-red-700">
              This password reset link is invalid, expired, or has already been used. Request a new link to continue.
            </p>
            <button
              type="button"
              onClick={onRequestAnotherLink}
              className="min-h-[48px] w-full rounded-md bg-mainPink px-4 py-3 font-medium text-white transition-colors hover:bg-mainBlue"
            >
              Request another reset link
            </button>
          </div>
        )}
        {!success && (
          <button type="button" onClick={onGoToLogin} className="mt-4 min-h-[44px] text-sm font-medium text-mainBlue hover:underline">
            Back to login
          </button>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;